/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button, Col, Row, Space, Table } from 'antd'
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { RouteChildrenProps } from "react-router"
import { Link } from 'react-router-dom'
import { MitigationType, ReportStatesEnum } from '../../../../constants/constants'
import { ROUTE_PATHS } from "../../../../constants/url-config"
import { ScenarioModel } from "../../../../models/scenario.model"
import { ERMService } from "../../../../services/erm.service"
import { DATE_UTIL } from "../../../../utils/dateUtils"
import i18n from "../../../../utils/i18n"
import { ROLE_GUARD } from '../../../../utils/role-guard'
import UTIL from '../../../../utils/utils'
import { AddIcon } from '../../../atoms/icon'
import "./scenarios.scss"


interface Props extends RouteChildrenProps { }

const Scenarios = (props: Props) => {

    const { t } = useTranslation();
    const [data, setData] = useState<ScenarioModel[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    const columns = [
        {
            title: i18n.t("Scenario.shortScenarioName"),
            dataIndex: 'short_name_for_scenario',
            key: 'short_name_for_scenario',
            render: (text: string, record: ScenarioModel) =>
                <Link className="blue-text"
                    to={record.submit_state === ReportStatesEnum.Draft && ROLE_GUARD.isCurrentUser(record.created_by) ?
                        `${ROUTE_PATHS.EditScenario}/${record.id}` :
                        `${ROUTE_PATHS.ViewScenario}/${record.id}`}>{text}
                </Link>
        },
        {
            title: i18n.t("Scenario.author"),
            dataIndex: 'created_by',
            key: 'created_by',
        },
        {
            title: i18n.t("Scenario.planningCycle"),
            dataIndex: 'planing_cycle',
            key: 'planing_cycle',
            render: (date: string) => DATE_UTIL.convertServerToMonthViewFormat(date)
        },
        {
            title: i18n.t("Scenario.status"),
            dataIndex: 'submit_state',
            key: 'submit_state',
            render: (text: string) => {
                const dom = UTIL.getLossStatesDOM(text);
                return (<span style={{ color: dom.color }}>
                    {dom.text}
                </span>)
            }
        },
        {
            title: i18n.t("Scenario.dateCreated"),
            dataIndex: 'created_on',
            key: 'created_on',
            render: (date: string) => DATE_UTIL.convertServerToViewFormat(date)
        },
        {
            title: i18n.t("Scenario.lbRelatedForms"),
            dataIndex: 'related_forms',
            key: 'related_forms',
            render: (text: string, record: ScenarioModel) => {
                if (record.submit_state === ReportStatesEnum.Approved) {
                    return (
                        <Space>
                            {
                                ROLE_GUARD.isCurrentUser(record.created_by) && (!record.scenario_mitigation || !record.scenario_mitigation.id) && <a className="related-action" onClick={(e) => { startMitigation(e, record.id) }}>{t("Scenario.lbStartMitigation")}</a>
                            }

                            {
                                (record.scenario_mitigation && record.scenario_mitigation.id) &&
                                <a className="related-action" onClick={(e) => { mitigation(e, record) }}>{t("Scenario.lbMitigation")}</a>
                            }

                            {
                                (record.appraisal_tool && record.appraisal_tool.id) && <a className="related-action" onClick={(e) => { projectAppraisal(e, record) }} >{t("Scenario.lbProjectAppraisal")}</a>
                            }

                        </Space>)
                }

                return null;
            }
        }
    ];

    const mitigation = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, record: ScenarioModel) => {
        e.stopPropagation();
        if (!record.scenario_mitigation) {
            return;
        }
        if (ROLE_GUARD.isCurrentUser(record.created_by) && UTIL.checkCanEditByState(record.scenario_mitigation.submit_state)) {
            props.history.push(`${ROUTE_PATHS.ScenarioMitigation}/${record.id}`);
        } else {
            props.history.push(`${ROUTE_PATHS.ScenarioMitigationView}/${record.id}`);
        }
    }

    const startMitigation = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: number | undefined) => {
        e.stopPropagation();

        if (id) {
            props.history.push({
                pathname: `${ROUTE_PATHS.ScenarioMitigation}/${id}`,
                search: `?type=${MitigationType.Start}`
            });
        }
    }

    const projectAppraisal = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, record: ScenarioModel) => {
        e.stopPropagation();

        if (!record.appraisal_tool) {
            return;
        }

        if (ROLE_GUARD.isCurrentUser(record.created_by) && UTIL.checkCanEditByState(record.appraisal_tool.submit_state)) {
            props.history.push(`${ROUTE_PATHS.ScenarioAppraisal}/${record.id}`);
        } else {
            props.history.push(`${ROUTE_PATHS.ScenarioAppraisalView}/${record.id}`);
        }
    }

    const addNew = () => {
        props.history.push(ROUTE_PATHS.AddScenario);
    }

    const rowClick = (record: ScenarioModel) => {
        if (record.submit_state === ReportStatesEnum.Draft) {
            props.history.push(`${ROUTE_PATHS.EditScenario}/${record.id}`);
        } else {
            props.history.push(`${ROUTE_PATHS.ViewScenario}/${record.id}`);
        }
    }

    const invite = () => {
        props.history.push(ROUTE_PATHS.ScenrarioInvite);
    }

    const getData = () => {
        setLoadingData(true);
        ERMService.getLossScenariosAsync().then(res => {
            setData(res.data);
        }).finally(() => {
            setLoadingData(false);
        });
    }

    // component did amount method
    useEffect(() => {
        getData();
    }, [])

    return (
        <div className='control-failure-wrapper'>
            <Row className="header-page">
                <div className="flex-1">
                    <h4>
                        {t("Scenario.title")}
                    </h4>
                </div>


                <Space size="middle" className='action-box'>
                    <Button shape="round" className="btn-border" onClick={invite}>
                        {t("Scenario.btnInvite")}
                    </Button>
                    <Button type="primary" shape="round" onClick={addNew}>
                        <AddIcon className="btn-icon" /> {t("Scenario.btnAdd")}
                    </Button>
                </Space>
            </Row>
            <Row>
                <Col span={24}>
                    <Table
                        loading={loadingData}
                        onRow={(record, rowIndex) => ({ onClick: event => { rowClick(record) } })}
                        dataSource={data}
                        columns={columns}
                        rowKey="id" />
                </Col>
            </Row>
        </div>

    )
}

export default Scenarios;